import { API_BASE_URL } from '@src/runtimeConfig'
import { useState } from 'react'
import { MyChip, MyDetailView } from '@interstellar-component'
import SimpleBar from 'simplebar-react'
import { formatFileSize } from '@src/services/Helper'
import FileViewer from '@src/components/FileViewer'
import AttachmentRow from '@src/components/AttachmentRow'
import AgreementCard from '../AgreementCard'

function rowKey(label) {
  if (!label) return ''
  return label.charAt(0).toLowerCase() + label.slice(1)
}

function formatDisplayValue(value) {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') {
    if (value && typeof value.name === 'string') return value.name
    try {
      return JSON.stringify(value)
    } catch {
      return '—'
    }
  }
  return String(value)
}

function absoluteFileUrl(path) {
  if (!path || typeof path !== 'string') return ''
  if (/^https?:\/\//i.test(path)) return path
  // Stored file paths already include the service homepage prefix
  // (e.g. "/backoffice/uploads/.."), which VITE_API_BASE_URL also contains —
  // prepend only the ORIGIN to avoid a doubled "/backoffice/backoffice".
  const apiBase = API_BASE_URL ?? ''
  let origin
  try {
    origin = new URL(apiBase, window.location.origin).origin
  } catch {
    origin = apiBase.replace(/\/+$/, '')
  }
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

function getCompanyInfo(detail) {
  const ci = detail?.company_information
  return ci && typeof ci === 'object' ? ci : {}
}

function getCompliance(detail) {
  const cd = detail?.compliance_documents
  return cd && typeof cd === 'object' ? cd : {}
}

const COMPLIANCE_DOC_KEYS = ['npwp_doc', 'nib_doc', 'akta_pendirian', 'akta_perubahan_terbaru']

function buildCompanyInformationSection(detail) {
  const ci = getCompanyInfo(detail)
  const cd = getCompliance(detail)

  const rows = [
    ['Company Legal Name', formatDisplayValue(ci.legal_name)],
    ['Business / Brand Name', formatDisplayValue(ci.name)],
    ['Business Category', formatDisplayValue(ci.business_category)],
    ['Industry', formatDisplayValue(ci.industry)],
    ['Number of Employees', formatDisplayValue(ci.number_of_employees)],
    ['Company Website', formatDisplayValue(ci.company_website)],
    ['Company Email', formatDisplayValue(ci.email)],
    ['Company Address', formatDisplayValue(ci.company_address)],
    ['NPWP Number', formatDisplayValue(cd.npwp)],
    ['NIB Number', formatDisplayValue(cd.nib)],
    ['Date of Establishment', formatDisplayValue(cd.date_of_establishment)],
  ]

  return rows.reduce((acc, [label, value]) => {
    acc[rowKey(label)] = value
    return acc
  }, {})
}

function buildAuthorizedRepresentativeSection(rep) {
  const r = rep && typeof rep === 'object' ? rep : {}
  const pick = (...keys) => {
    const hit = keys.find((k) => {
      const v = r[k]
      return v != null && v !== ''
    })
    return hit != null ? formatDisplayValue(r[hit]) : '—'
  }

  const rows = [
    ['Full Name', pick('name', 'full_name', 'rep_name')],
    ['Job Title', pick('job_title', 'position', 'rep_position')],
    ['Email Address', pick('email_address', 'email', 'rep_email')],
    ['Phone Number', pick('phone_number', 'phone', 'rep_phone')],
    // ID Type / ID Number hidden until PIC identity capture is built (no input
    // for them yet, so they always render "—").
  ]

  return rows.reduce((acc, [label, value]) => {
    acc[rowKey(label)] = value
    return acc
  }, {})
}

function listComplianceDocuments(detail) {
  const cd = getCompliance(detail)
  return COMPLIANCE_DOC_KEYS.map((key) => {
    const doc = cd[key]
    if (!doc || typeof doc !== 'object' || !doc.file_path) return null
    return {
      key,
      file_name: doc.file_name ?? doc.title ?? key,
      file_path: doc.file_path,
      file_size: doc.file_size,
      mime_type: doc.mime_type,
      status: doc.status,
    }
  }).filter(Boolean)
}

export default function Information({ data: detail, loading }) {
  const companyDatas = buildCompanyInformationSection(detail)
  const picDatas = buildAuthorizedRepresentativeSection(detail?.authorized_representative)
  const complianceFiles = listComplianceDocuments(detail)
  // { url, fileName, mimeType } for the in-app file preview, or null.
  const [viewerDoc, setViewerDoc] = useState(null)

  const industryKey = rowKey('Industry')
  const businessCategoryKey = rowKey('Business Category')

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center px-4 py-12 text-sm text-gray-light/600">
        Loading…
      </div>
    )
  }

  return (
    <SimpleBar forceVisible="y" style={{ height: '100%' }}>
      <div className="flex min-h-full flex-1 flex-col gap-6 bg-gray-50 pb-8 pt-4">
        <div className="flex flex-col gap-6 px-4">
          <MyDetailView
            header="Company Information"
            datas={companyDatas}
            func={{
              [industryKey]: (value) =>
                value === '—' ? (
                  '—'
                ) : (
                  <MyChip
                    label={value}
                    rounded="lg"
                    color="modern"
                    variant="outlined"
                    size="sm"
                  />
                ),
              [businessCategoryKey]: (value) =>
                value === '—' ? (
                  '—'
                ) : (
                  <MyChip
                    label={value}
                    rounded="lg"
                    color="modern"
                    variant="outlined"
                    size="sm"
                  />
                ),
            }}
          />

          <MyDetailView
            header="Authorized Representative (PIC)"
            datas={picDatas}
          />

          {complianceFiles.length > 0 ? (
            <div className="flex flex-col gap-[2px] overflow-hidden rounded-xl border border-gray-200 bg-[#fdfdfd] shadow-sm">
              <div className="flex items-center justify-between px-5 pb-2 pt-3">
                <h3 className="text-sm font-semibold text-gray-900">Attachments</h3>
              </div>
              <div className="mx-[1px] mb-[1px] overflow-hidden rounded-[12px] border border-gray-200 bg-white shadow-sm">
                {complianceFiles.map((doc, index) => (
                  <AttachmentRow
                    key={doc.key}
                    name={doc.file_name || doc.document_type}
                    mimeType={doc.mime_type}
                    meta={doc.file_size ? formatFileSize(Number(doc.file_size)) : null}
                    onClick={() =>
                      setViewerDoc({
                        url: absoluteFileUrl(doc.file_path),
                        fileName: doc.file_name || doc.document_type,
                        mimeType: doc.mime_type,
                      })
                    }
                    last={index === complianceFiles.length - 1}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <AgreementCard company={detail} onView={setViewerDoc} />
        </div>
      </div>
      <FileViewer
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        url={viewerDoc?.url}
        fileName={viewerDoc?.fileName}
        mimeType={viewerDoc?.mimeType}
      />
    </SimpleBar>
  )
}
